import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "@/pages/auth/login";
import ForgotPasswordPage from "@/pages/auth/forgot-password";
import ResetPasswordPage from "@/pages/auth/reset-password";
import CreateAccountPage from "@/pages/auth/create-account";
import DashboardLayout from "@/components/dashboard-layout";
import HomePage from "@/pages/dashboard/home";
import InvitePage from "@/pages/invite";
import OnboardingPage from "@/pages/onboarding";
import SettingsPage from "@/pages/settings";
import AccessPage from "@/pages/access";
import LimitsPage from "@/pages/limits";
import AmenitiesPage from "@/pages/amenities";
import ClubRulesPage from "@/pages/club-rules";
import IntegrationPage from "@/pages/integration";
import CourtsPage from "@/pages/courts";
import SportsPage from "@/pages/sports";
import ClubsPage from "@/pages/clubs";
import BookingsPage from "@/pages/bookings";
import AccountPage from "@/pages/account";
import PlayersPage from "@/pages/players";
import AdminsPage from "@/pages/admins";
import TimeSlotsPage from "@/pages/time-slots";
import CoachesPage from "@/pages/coaches";
import GearPage from "@/pages/gear";
import CompetitionsPage from "@/pages/competitions";
import CoachSchedulePage from "@/pages/coaching";
import HelpPage from "@/pages/help";
import ReviewsPage from "@/pages/reviews";
import ProtectedRoute from "@/components/protected-route";
import OnboardingGate from "@/components/onboarding-gate";
import { MeProvider, useMe } from "@/lib/me";

// The dashboard index. Coaches have no dashboard — send them to their schedule.
function IndexRoute() {
  const { user } = useMe();
  if (user?.role === "COACH") return <Navigate to="/coaching" replace />;
  return <HomePage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/create-account" element={<CreateAccountPage />} />

        {/* Onboarding (full-screen, no sidebar) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <MeProvider>
                <OnboardingPage />
              </MeProvider>
            </ProtectedRoute>
          }
        />

        {/* Protected dashboard */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MeProvider>
                <OnboardingGate>
                  <DashboardLayout />
                </OnboardingGate>
              </MeProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<IndexRoute />} />
          <Route
            path="bookings"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN", "STAFF"]}>
                <BookingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="account" element={<AccountPage />} />
          <Route
            path="invite"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <InvitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="coaches"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <CoachesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="coaching"
            element={
              <ProtectedRoute allowedRoles={["COACH"]}>
                <CoachSchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="access"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <AccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="limits"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <LimitsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="amenities"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <AmenitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="gear"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <GearPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="competitions"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <CompetitionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="club-rules"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <ClubRulesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="integration"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <IntegrationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="courts"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <CourtsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="sports"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <SportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs"
            element={
              <ProtectedRoute allowedRoles={["SUPERADMIN"]}>
                <ClubsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="players"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <PlayersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admins"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <AdminsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="time-slots"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <TimeSlotsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="reviews"
            element={
              <ProtectedRoute allowedRoles={["MANAGER", "SUPERADMIN"]}>
                <ReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route path="help" element={<HelpPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
